import { useState, useEffect, useCallback } from 'react';
import { getMyTutorAnalytics, getStudentTutorAnalytics } from '../api/tutorAnalyticsApi';
import type { TutorAnalyticsSummary } from '../types/tutorAnalytics';

interface UseTutorAnalyticsOptions {
  studentId?: string;
  initialDays?: number;
}

interface UseTutorAnalyticsReturn {
  data: TutorAnalyticsSummary | null;
  loading: boolean;
  error: string | null;
  days: number;
  setDays: (days: number) => void;
  refetch: () => void;
}

export function useTutorAnalytics(
  opts: UseTutorAnalyticsOptions = {}
): UseTutorAnalyticsReturn {
  const { studentId, initialDays = 30 } = opts;
  const [data, setData] = useState<TutorAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(initialDays);

  const fetchData = useCallback(async (daysParam: number) => {
    setLoading(true);
    setError(null);

    try {
      const result = studentId
        ? await getStudentTutorAnalytics(studentId, daysParam)
        : await getMyTutorAnalytics(daysParam);

      // Ensure the response has the expected structure
      const validatedResult = {
        ok: result.ok || true,
        total_tutor_minutes: result.total_tutor_minutes || 0,
        sessions_count: result.sessions_count || 0,
        last_active_at: result.last_active_at || null,
        latest_speaking_level: result.latest_speaking_level || null,
        latest_overall_score: result.latest_overall_score || null,
        trend: result.trend || [],
        last_lesson_no: result.last_lesson_no || null,
        last_topic_label: result.last_topic_label || null,
        speaking_sessions: result.speaking_sessions || [],
        daily_engagement: result.daily_engagement || [],
        session_history: result.session_history || [],
      };

      setData(validatedResult);
    } catch (err: any) {
      let message = 'Failed to load analytics';

      if (err.response?.status === 401) {
        message = 'Please login again';
      } else if (err.response?.status === 403) {
        message = 'Access denied - analytics may not be available for your role';
      } else if (err.response?.status === 400) {
        message = 'Tenant missing or invalid request';
      } else if (err.response?.status === 404) {
        message = 'Analytics service not found - feature may still be in development';
      } else if (err.response?.status === 500) {
        message = 'Server error - analytics service may be temporarily unavailable';
      } else if (!err.response) {
        message = 'Cannot connect to analytics service';
      }

      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  const refetch = useCallback(() => {
    fetchData(days);
  }, [fetchData, days]);

  const handleSetDays = useCallback((newDays: number) => {
    setDays(newDays);
  }, []);

  useEffect(() => {
    fetchData(days);
  }, [days, fetchData]);

  return {
    data,
    loading,
    error,
    days,
    setDays: handleSetDays,
    refetch,
  };
}